/**
 * Trabajadores: listado y alta.
 *
 * No hay registro público: a un trabajador lo da de alta su empresa. La
 * contraseña se escribe aquí una vez y se le comunica en mano; el backend
 * la guarda hasheada y no la devuelve jamás.
 */
import { useCallback, useEffect, useState } from 'react';
import { AdminLayout } from './AdminLayout';
import {
  getUsers,
  createUser,
  updateUser,
  getWorkPolicies,
  reissueActivationQr,
  resendInvitation,
  AdminUser,
  AdminUserCreate,
  WorkPolicyView,
  apiMessage,
} from '../../services/apiClient';
import { Icon } from '../ui/Icon';
import { EmptyState } from '../ui/EmptyState';
import { WorkerFormFields } from './WorkerFormFields';
import { ActivationQr } from './ActivationQr';

const EMPTY: AdminUserCreate = {
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  role: 'EMPLOYEE',
  weekly_hours: 40,
  is_part_time: false,
};

export function WorkersAdmin() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [policies, setPolicies] = useState<WorkPolicyView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState<AdminUserCreate>(EMPTY);
  const [saving, setSaving] = useState(false);

  // Edición por fila: mismo WorkerFormFields en mode="edit", con su propio draft.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<AdminUserCreate>(EMPTY);
  const [savingEdit, setSavingEdit] = useState(false);

  // Baja/reactivar: is_active por fila. No es un borrado.
  const [busyId, setBusyId] = useState<number | null>(null);

  // Alta SIN correo: el token de activación sólo viaja en esta respuesta,
  // así que se enseña el QR aquí mismo, una vez, antes de volver a la lista.
  const [activated, setActivated] = useState<{ id: number; token: string; nombre: string } | null>(
    null,
  );

  // Reemisión ("se perdió el QR"): el backend no devuelve ningún token, ya
  // sólo el PNG — así que aquí se guarda el Blob resuelto, no un token.
  const [reissuingId, setReissuingId] = useState<number | null>(null);
  const [reissuedQr, setReissuedQr] = useState<{ id: number; blobUrl: string; nombre: string } | null>(
    null,
  );

  // Reenvío de la invitación POR CORREO (la invitación que no llegó, o la
  // que se reemite al corregir una errata): el backend reemite el enlace y
  // responde `sent`, que aquí sólo se enseña cuando salió mal.
  const [resendingId, setResendingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    const [u, p] = await Promise.all([getUsers(), getWorkPolicies().catch(() => [])]);
    setUsers(u);
    setPolicies(p);
  }, []);

  useEffect(() => {
    load().catch(() => setError('No se pudo cargar la plantilla'));
  }, [load]);

  const set = <K extends keyof AdminUserCreate>(k: K, v: AdminUserCreate[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setError(null);
    // El `required` del input no dispara: aquí no hay <form>, se guarda con
    // botón onClick. La fecha de alta se comprueba aquí (y la API también la
    // exige): sin ella, el motor devenga el año completo de vacaciones.
    if (!draft.hire_date) {
      setError('La fecha de alta es obligatoria: de ella depende el devengo de vacaciones');
      return;
    }
    setSaving(true);
    try {
      const creado = await createUser({
        ...draft,
        // Vacío = sin correo (el trabajador entra por QR), nunca "" contra el backend.
        email: draft.email || undefined,
        // Contraseña vacía = invitación por email (el trabajador crea la suya).
        // Sin correo NUNCA se manda: no hay pantalla donde escribirla, y el
        // alta con las dos cosas dejaba a la persona sin PIN y sin QR (el
        // backend la rechaza con 422; el formulario ni la enseña, pero puede
        // quedar escrita de antes de borrar el correo).
        password: draft.email ? draft.password || undefined : undefined,
        work_policy_id: draft.work_policy_id || policies.find((p) => p.is_default)?.id,
      });
      setShowForm(false);
      setDraft(EMPTY);
      await load();
      // El envío es best-effort: si el SMTP no pasó, se dice aquí — antes la
      // invitación que no salió se daba por enviada y sólo se recuperaba
      // cambiando el correo a otra dirección y volviendo.
      if (creado.email_sent === false) {
        setError(
          `No se pudo enviar el correo a ${creado.email} — reenvíalo desde su ficha`,
        );
      }
      // Sólo el alta SIN correo devuelve el token: es la única vez que existe.
      if (creado.activation_token) {
        setActivated({
          id: creado.id,
          token: creado.activation_token,
          nombre: `${creado.first_name} ${creado.last_name}`,
        });
      }
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo dar de alta'));
    } finally {
      setSaving(false);
    }
  };

  // Contraseña vacía = invitación por email (el trabajador crea la suya).
  // Sin correo sólo vale para un trabajador (los responsables necesitan
  // correo para los avisos: el backend lo rechaza con 422, aquí se evita el viaje).
  const canSave =
    (!!draft.email || draft.role === 'EMPLOYEE') &&
    (!draft.password || draft.password.length >= 8) &&
    draft.first_name &&
    draft.last_name;

  const setEdit = <K extends keyof AdminUserCreate>(k: K, v: AdminUserCreate[K]) =>
    setEditDraft((d) => ({ ...d, [k]: v }));

  const openEdit = (u: AdminUser) => {
    setShowForm(false);
    setError(null);
    setEditingId(u.id);
    setEditDraft({
      email: u.email ?? undefined,
      first_name: u.first_name,
      last_name: u.last_name,
      role: u.role,
      // El DNI se precarga: el input de edición mostraba el campo VACÍO
      // aunque el trabajador tuviera uno (mienta sobre el estado real) y lo
      // tecleado se descartaba en silencio al guardar — con el botón en
      // verde. El backend ya soporta editarlo (UserUpdate.dni, mismo
      // validador que el alta); el panel ni lo traía ni lo mandaba
      // (ronda 4, r2b14[4]).
      dni: u.dni ?? '',
      employee_code: u.employee_code ?? '',
      hire_date: u.hire_date ?? '',
      weekly_hours: u.weekly_hours,
      is_part_time: u.is_part_time,
      password: '',
    });
  };

  const closeEdit = () => {
    setEditingId(null);
    setEditDraft(EMPTY);
    setError(null);
  };

  const saveEdit = async () => {
    if (editingId == null) return;
    setError(null);
    setSavingEdit(true);
    try {
      const guardado = await updateUser(editingId, {
        first_name: editDraft.first_name,
        last_name: editDraft.last_name,
        role: editDraft.role,
        // El DNI corregido por fin viaja: es lo que el informe del art. 34.9
        // usa para distinguir a dos personas con el mismo nombre, y una errata
        // del alta quedaba fija en el papel para Inspección (r2b14[4]).
        dni: editDraft.dni ?? undefined,
        employee_code: editDraft.employee_code,
        weekly_hours: editDraft.weekly_hours,
        is_part_time: editDraft.is_part_time,
        // Contraseña vacía = no cambiarla (reset solo si se rellena). A quien
        // no tiene correo no se le manda nunca: su credencial es el PIN y
        // guardar una contraseña le borraría el centinela (409 del backend).
        password: editDraft.email ? editDraft.password || undefined : undefined,
        // La fecha de alta viaja sólo si trae valor: el draft se inicia con
        // `u.hire_date ?? ''` y la cadena vacía significa "no tocar". Así se
        // puede CORREGIR la de un legacy sin fecha, pero no se fuerza.
        hire_date: editDraft.hire_date || undefined,
      });
      closeEdit();
      await load();
      // La reinvitación por corrección de correo también es best-effort:
      // mismo aviso que el alta.
      if (guardado.email_sent === false) {
        setError(
          `No se pudo enviar el correo a ${guardado.email} — reenvíalo desde su ficha`,
        );
      }
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudieron guardar los cambios'));
    } finally {
      setSavingEdit(false);
    }
  };

  const canSaveEdit =
    editDraft.first_name && editDraft.last_name && (!editDraft.password || editDraft.password.length >= 8);

  const toggleActive = async (u: AdminUser) => {
    setError(null);
    setBusyId(u.id);
    try {
      await updateUser(u.id, { is_active: !u.is_active });
      await load();
    } catch (e: unknown) {
      // Guard del último admin: el backend responde 409 con un mensaje explicativo.
      setError(apiMessage(e, 'No se pudo actualizar el estado'));
    } finally {
      setBusyId(null);
    }
  };

  const reissueQr = async (u: AdminUser) => {
    setError(null);
    setReissuingId(u.id);
    try {
      const blob = await reissueActivationQr(u.id);
      setReissuedQr({
        id: u.id,
        blobUrl: URL.createObjectURL(blob),
        nombre: `${u.first_name} ${u.last_name}`,
      });
      closeEdit();
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo reemitir el QR'));
    } finally {
      setReissuingId(null);
    }
  };

  const resendInvite = async (u: AdminUser) => {
    setError(null);
    setResendingId(u.id);
    try {
      const res = await resendInvitation(u.id);
      if (!res.sent) {
        setError(
          `No se pudo enviar el correo a ${u.email} — comprueba el correo de salida y vuelve a intentarlo`,
        );
      }
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo reenviar la invitación'));
    } finally {
      setResendingId(null);
    }
  };

  const closeReissuedQr = () => {
    // El object URL lo creó `reissueQr`: se revoca aquí, no en ActivationQr,
    // que sólo lo muestra (ver `preloadedBlobUrl`).
    if (reissuedQr) URL.revokeObjectURL(reissuedQr.blobUrl);
    setReissuedQr(null);
  };

  if (reissuedQr) {
    return (
      <AdminLayout title="Trabajadores">
        <div className="max-w-[420px] mx-auto space-y-3">
          <p className="rounded-xl bg-warn-soft text-warn px-4 py-3 text-[13.5px]">
            El código anterior deja de valer: a partir de ahora sólo sirve este QR nuevo.
          </p>
          <ActivationQr
            userId={reissuedQr.id}
            preloadedBlobUrl={reissuedQr.blobUrl}
            nombre={reissuedQr.nombre}
            titulo={`QR nuevo para ${reissuedQr.nombre}`}
            onClose={closeReissuedQr}
          />
        </div>
      </AdminLayout>
    );
  }

  if (activated) {
    return (
      <AdminLayout title="Trabajadores">
        <div className="max-w-[420px] mx-auto">
          <ActivationQr
            userId={activated.id}
            token={activated.token}
            nombre={activated.nombre}
            onClose={() => setActivated(null)}
          />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Trabajadores">
      <div className="max-w-[960px] space-y-4">
        {error && (
          <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14px]">{error}</p>
        )}

        <div className="flex justify-end">
          <button
            onClick={() => {
              if (!showForm) closeEdit();
              setShowForm(!showForm);
            }}
            className="h-[42px] px-4.5 px-5 rounded-xl bg-brand text-on-brand font-bold text-[14px] flex items-center gap-2"
          >
            <Icon name="dots" size={18} strokeWidth={2} />
            {showForm ? 'Cerrar' : 'Dar de alta'}
          </button>
        </div>

        {showForm && (
          <div className="card-surface space-y-3.5 animate-rise">
            <WorkerFormFields draft={draft} set={set} mode="create" />
            <button onClick={save} disabled={!canSave || saving} className="btn-primary h-12 text-[15px]">
              {saving ? 'Guardando…' : 'Dar de alta'}
            </button>
          </div>
        )}

        <div className="rounded-2xl bg-surface border border-line overflow-x-auto">
          <div className="grid grid-cols-[1.4fr_.8fr_.8fr_.8fr_auto] gap-3 px-4.5 px-5 py-3 bg-surface-2 font-mono text-[11px] uppercase text-ink-3 min-w-[820px]">
            <span>Trabajador</span>
            <span>Código</span>
            <span>Jornada</span>
            <span>Rol</span>
            <span>Acciones</span>
          </div>
          {users.map((u) => (
            <div key={u.id} className="border-t border-line">
              {editingId === u.id ? (
                <div className="p-5 space-y-3.5 bg-surface-2 animate-rise min-w-[820px]">
                  <p className="font-bold text-[15px]">
                    Editar a {u.first_name} {u.last_name}
                  </p>
                  <WorkerFormFields draft={editDraft} set={setEdit} mode="edit" />
                  {u.email == null && !u.is_verified && (
                    <div className="rounded-xl border border-line-strong p-4 space-y-2.5">
                      <p className="text-[13.5px] text-ink-2">
                        Todavía no ha activado su cuenta. Si el QR que le diste al darle de
                        alta se perdió sin escanear, reemite uno nuevo — el anterior dejará de valer.
                      </p>
                      <button
                        onClick={() => reissueQr(u)}
                        disabled={reissuingId === u.id}
                        className="h-10 px-4 rounded-lg border border-line-strong font-semibold text-[13px] disabled:opacity-40"
                      >
                        {reissuingId === u.id ? 'Generando…' : 'Reemitir QR de activación'}
                      </button>
                    </div>
                  )}
                  {u.email != null && !u.is_verified && (
                    <div className="rounded-xl border border-line-strong p-4 space-y-2.5">
                      <p className="text-[13.5px] text-ink-2">
                        Todavía no ha activado su cuenta. Si la invitación no le llegó —o
                        el envío falló en su día—, reenvíala: el enlace anterior dejará
                        de valer.
                      </p>
                      <button
                        onClick={() => resendInvite(u)}
                        disabled={resendingId === u.id}
                        className="h-10 px-4 rounded-lg border border-line-strong font-semibold text-[13px] disabled:opacity-40"
                      >
                        {resendingId === u.id ? 'Enviando…' : 'Reenviar invitación'}
                      </button>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={saveEdit}
                      disabled={!canSaveEdit || savingEdit}
                      className="btn-primary h-12 text-[15px] max-w-[280px]"
                    >
                      {savingEdit ? 'Guardando…' : 'Guardar cambios'}
                    </button>
                    <button
                      onClick={closeEdit}
                      disabled={savingEdit}
                      className="h-12 px-5 rounded-xl border border-line-strong font-semibold text-[14px]"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-[1.4fr_.8fr_.8fr_.8fr_auto] gap-3 px-5 py-3.5 items-center min-w-[820px]">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-[34px] h-[34px] rounded-lg bg-surface-2 grid place-items-center font-display font-bold text-[13px] text-ink-2 shrink-0">
                      {u.first_name[0]}
                      {u.last_name[0]}
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-[14.5px] truncate">
                        {u.first_name} {u.last_name}
                        {!u.is_active && <span className="text-ink-3 font-normal"> · baja</span>}
                      </p>
                      <p className="text-[12px] text-ink-3 truncate">{u.email ?? 'Sin correo · entra por QR'}</p>
                    </div>
                  </div>
                  <span className="font-mono text-[13px] text-ink-2">{u.employee_code ?? '—'}</span>
                  <span className="text-[13.5px]">
                    {u.weekly_hours} h{u.is_part_time && <span className="text-ink-2"> · parcial</span>}
                  </span>
                  <span className="text-[13px] text-ink-2">
                    {{ ADMIN: 'Admin', MANAGER: 'Responsable', EMPLOYEE: 'Trabajador' }[u.role]}
                  </span>
                  <div className="flex items-center gap-2 justify-end shrink-0">
                    <button
                      onClick={() => openEdit(u)}
                      className="h-9 px-3 rounded-lg border border-line-strong font-semibold text-[12.5px] flex items-center gap-1.5"
                    >
                      <Icon name="edit" size={14} strokeWidth={1.8} />
                      Editar
                    </button>
                    <button
                      onClick={() => toggleActive(u)}
                      disabled={busyId === u.id}
                      className={`h-9 px-3 rounded-lg border border-line-strong font-semibold text-[12.5px] disabled:opacity-40 ${
                        u.is_active ? 'text-danger' : 'text-brand'
                      }`}
                    >
                      {busyId === u.id ? '…' : u.is_active ? 'Dar de baja' : 'Reactivar'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {users.length === 0 && (
            <EmptyState
              icon="users"
              title="Aún no hay trabajadores"
              body="Da de alta a tu equipo o invítalos por email para que puedan fichar desde el móvil o el kiosco."
              ctaLabel="Dar de alta"
              onCta={() => setShowForm(true)}
            />
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
